import React, { FC, useCallback, useContext } from 'react';
import classNames from 'classnames';
import XIcon from 'icons/X.icon';
import { Button } from 'ncoded-component-library';
import AuthContext from 'providers/Auth/Auth.context';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-final-form';
import {
  isEnterPressed,
  isOpenDrawerOrModal,
  scrollToErrorsOnSubmit,
} from 'utils';
import FloatingActions from 'components/FloatingActions';
import useKeyDown from 'hooks/useKeyDown';

import './FormWrapper.styles.scss';

type FormWrapperProps = {
  className?: string;
  exitRoute?: string;
  currentStep?: number;
  title: string;
  disabledSubmit: boolean;
  isFloatingVisible: boolean;
  isValid: boolean;
  submitText?: string;
  onBack?: () => void;
};

const ALLOWED_HTML_TAG_FOR_ENTER_SUBMIT = ['BODY', 'INPUT'];

const FormWrapper: FC<FormWrapperProps> = ({
  className,
  children,
  title,
  exitRoute,
  disabledSubmit,
  submitText,
  isValid,
  isFloatingVisible,
  currentStep = 0,
  onBack,
}) => {
  const { navigateBack } = useContext(AuthContext);

  const { submit } = useForm();

  const { t } = useTranslation();

  const classes = classNames('form-wrapper', className);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isOpenDrawerOrModal() || !isEnterPressed(e)) return;

      const { tagName } = document.activeElement;

      const targetClassLists = Array.from((e.target as Element).classList).join(
        ',',
      );

      const isSelect = targetClassLists.includes('ncoded-select');

      if (!isSelect && ALLOWED_HTML_TAG_FOR_ENTER_SUBMIT.includes(tagName))
        scrollToErrorsOnSubmit(!isValid, submit);
    },
    [isValid, submit],
  );

  useKeyDown({ onKeyDown });

  return (
    <div className={classes}>
      <div className="form-wrapper__header">
        <div>
          <h1>{title}</h1>
          <Button
            variant="icon"
            aria-label="Close"
            icon={XIcon}
            onClick={() =>
              navigateBack({ replace: true, fallbackRoute: exitRoute })
            }
          />
        </div>
      </div>

      <div className="form-wrapper__content">
        <div>{children}</div>
      </div>

      <FloatingActions
        disabled={disabledSubmit}
        message={t(!isValid ? 'formHasMissingFields' : 'formReadyToSubmit')}
        cancelTxt={t('back')}
        submitTxt={submitText}
        onSubmit={() => scrollToErrorsOnSubmit(!isValid, submit)}
        {...(currentStep !== 0 && { onCancel: onBack })}
      />
    </div>
  );
};

export default FormWrapper;
